import { button } from '@sparta.global.helpers/button-helper/dist/es6.js';
import { svgHelper } from '@sparta.global.helpers/svg-helper/dist/es6.js';
import Handlebars from '@sparta.global.vendors/handlebars';
import spartaGlobalParams from '@sparta.global.utilities/sparta-global-params-utility/dist/es6.js';

/**
 * Player account and player ID defaults
 */
const DEFAULTS = new function ClassNamesContructor() {
  this.kalturaAccountId = 4514833;
  this.kalturaPlayerId = 52188792;
  this.kalturaCDN = 'https://cdnapisec.kaltura.com';
}();

// Default global strings
const defaultStrings = {
  play: "Play video",
  replay: "Replay video",
  pause: "Pause video",
  rewind: "Rewind video",
  "fast-forward": "Fast-forward video",
  captions: {
    enable: {
      titleAttr: "Captions toggle",
      ariaLabel: "Enable closed captioning",
      string: "Press enter to enable closed captioning."
    },
    disable: {
      titleAttr: "Captions toggle",
      ariaLabel: "Disable closed captioning",
      string: "Press enter to disable closed captioning."
    }
  },
  fullscreen: {
    on: {
      titleAttr: "Full-screen toggle",
      string: "Press enter to enable full-screen mode"
    },
    off: {
      titleAttr: "Full-screen toggle",
      string: "Press enter to exit full-screen mode"
    }
  },
  transcript: {
    header: "Transcript",
    show: "View transcript",
    hide: "Hide transcript",
    collapsed: "Transcript collapsed",
    expanded: "Transcript expanded"
  },
  "time-elapsed": "00:00",
  "time-total": "00:00",
  "volume-up": "Volume up",
  "volume-down": "Volume down",
  "volume-control": {
    titleAttr: "Volume control",
    ariaLabel: "Press enter to activate volume control. Use the Up and Down arrow keys to adjust the volume."
  },
  status: {
    loadingStarted: "Loading video",
    loadingComplete: "Loading complete"
  }
};

/**
 * Splits the poster array from JCR to create a srcset attribute
 * @param {*} posterArray 
 * @returns 
 */
function createSrcSetPoster(posterArray) {
  let output = posterArray;
  let srcSet;
  let src;

  // Array most likely from helper
  if (posterArray && typeof posterArray === 'object' && Array.isArray(posterArray)) {
    output = posterArray.map(key => key).join(', ');
    srcSet = output;
    src = output.split(' ')[0];
  }
  // String most likely from utility
  else if (posterArray && typeof posterArray === 'string') {
    srcSet = output;
    src = output.split(' ')[0];
  }

  // Return img response
  return {
    srcSet,
    src
  };
}

/**
 * Builds the togglable chevron trigger and its transcript area below the video
 * @param {Object} config JCR json object
 * @param {Object} globalStrings Page/Site params
 */
function buildTranscript(config, globalStrings) {
  const {
    containerId,
    transcript
  } = config;
  const globalTranscriptStrings = globalStrings && globalStrings.transcript ? globalStrings.transcript : {};

  // Kill check
  if (!transcript) {
    return '';
  }

  // Chevron icon part of toggle
  const transcriptToggleChevron = svgHelper({
    id: `spaCinema_${containerId}_transcript-icon`,
    type: "static",
    iconName: "caretRight",
    ariaHidden: true
  });

  // Toggle button link

  const transcriptToggleBtn = button({
    classes: 'spa-cinema-video-transcript-trigger spa-btn--link',
    content: `${transcriptToggleChevron}${globalTranscriptStrings.show || 'View transcript'}`,
    attributes: {
      id: `spaCinema_${containerId}_transcript-button`,
      'aria-expanded': 'false',
      'aria-controls': `spaCinema_${containerId}_transcript-content`
    }
  });
  return `
    <div id="spaCinema_${containerId}_transcript" class="spa-cinema-video-transcript spa-cinema-metadata--link-transcript">
      ${transcriptToggleBtn}
      <div id="spaCinema_${containerId}_transcript-content" class="spa-cinema-video-transcript-content spa-cinema-metadata--transcript">
        ${transcript.map(item => `<p>${item}</p>`).join('')}
      </div>
    </div>
  `;
}

/**
 * @method helpers.cinemaHelper
 * @param {object} config JSON object used to build the chip group.
 * @param {object} context JSON object added by platform.
 * @returns {Node} `Handlebars.SafeString`.
 */
function cinemaHelper(config, context) {
  if (config && config.containerId && config.videoId) {
    // Create a unique ID if we've not been given one
    const {
      containerId,
      videoId,
      poster
    } = config;

    // Get global strings
    const globalStrings = spartaGlobalParams.getSpaParams({
      context,
      lookupKey: 'video',
      defaultObj: defaultStrings
    });

    // Create a list of attributes for the cms wrapper
    let {
      attributes = {}
    } = config;
    let attributesString = '';
    Object.keys(attributes).forEach(key => {
      if (typeof attributes[key] === 'string' || typeof attributes[key] === 'boolean') {
        attributesString += `${key}="${attributes[key]}"`;
      } else {
        attributesString += `${key}="${Handlebars.Utils.escapeExpression(JSON.stringify(attributes[key]))}"`;
      }
    });

    // Explicit rules
    const {
      options = {}
    } = config;
    options.autoEmbed = 'autoEmbed' in options ? `${options.autoEmbed}` : true;
    options.autoStage = 'autoStage' in options ? `${options.autoStage}` : true;

    // Player values
    // Note: In V6 helper context does not pass full spaParams which means this logic has to move to the utility
    options.accountId = config.kalturaAccountId || DEFAULTS.kalturaAccountId;
    options.playerID = config.kalturaPlayerId || DEFAULTS.kalturaPlayerId;
    options.environmentHost = config.environmentHost || DEFAULTS.kalturaCDN;
    options.videoId = videoId;
    options.hasPoster = typeof poster !== 'undefined' ? true : false;

    // Optional title
    const headingLevel = options.headingLevel || 'div';
    const addTitle = 'embedTitle' in options ? `${options.embedTitle}` : true;
    const title = options.title && addTitle ? `
          <${headingLevel} id="spaCinema_${containerId}Title" class="spa-cinema-title ${addTitle.toString() === 'false' ? 'hidden' : ''}">
            ${typeof options.title === 'string' ? `${options.title}` : ''}
          </${headingLevel}>
        ` : '';

    // Optional description
    const addDescription = 'embedDescription' in options ? `${options.embedDescription}` : true;
    const description = options.description && addDescription ? `
          <p id="spaCinema_${containerId}Description" class="spa-cinema-description ${addDescription.toString() === 'false' ? 'hidden' : ''}">
            ${typeof options.description === 'string' ? `${options.description}` : ''}
          </p>
        ` : '';

    // Aria described by attribute
    const ariaDescribedBy = `aria-describedby="spaCinema_${containerId}Title spaCinema_${containerId}Description"`;

    // Generate thumbnail on build from assets provided from JCR
    const posterThumbnail = poster ? cinemaThumbnailHelper({
      containerId,
      options,
      poster,
      globalStrings,
      autoEmbed: options.autoEmbed
    }) : '';

    // Build the final DOM:
    const output = `
      <div
        id="spaCinema_${containerId}"
        class="spa-cinema"
        data-options="${Handlebars.Utils.escapeExpression(JSON.stringify(options))}"
        tabindex="-1"
        ${ariaDescribedBy}
        ${attributesString}
      >
        <div class="spa-cinema-head">
          ${addTitle.toString() === 'true' ? title : ''}
          ${addDescription.toString() === 'true' ? description : ''}
          <div id="spaCinemaStatus" class="spa-cinema-status ada-hidden" role="status"></div>
        </div>
        <div class="spa-cinema-body">
          <div id="spaCinema_${containerId}_player" class="spa-cinema-video-container">
            ${posterThumbnail}
          </div>
        </div>
        <div class="spa-cinema-foot">
          ${addTitle.toString() === 'bottom' ? title : ''}
          ${addDescription.toString() === 'bottom' ? description : ''}
          ${buildTranscript(config, globalStrings)}
        </div>
      </div>
    `.trim();
    return new Handlebars.SafeString(`${output.replace(/[\t\n\r]/gm, '')}`);
  } else {
    return '';
  }
}

/**
 * Shareable button HTML to generate default thumbnail for video
 * @param {Object} config JCR 
 * @returns {Element} - DOM element
 */
function cinemaThumbnailHelper(config) {
  // Create a unique ID if we've not been given one
  const {
    containerId,
    poster,
    options,
    autoEmbed,
    globalStrings
  } = config;
  const {
    title
  } = options;
  const imgSources = createSrcSetPoster(poster);

  // Thumbnail img
  const imgOutput = `
    <img
      id="${containerId}ThumbnailLarge"
      class="spa-cinema-thumbnail--large"
      alt="${title ? title : ''}"
      srcset="${imgSources.srcSet}"
      src="${imgSources.src}"
    />
  `;

  // Thumbnail button icon
  const playBtnIcon = `
    <span
      class="spa-cinema-control--play-btn"
      type="button"
    >
      <div class="playkit-tooltip">
        <i class="playkit-icon playkit-icon-play"></i>
        <span class="ada-hidden">
          ${globalStrings && globalStrings.play ? globalStrings.play : 'Play video'}
        </span>
      </div>
    </span>
  `;

  // Thumbnail wrapped in button
  let output = `
    <button
      id="${containerId}ThumbnailLargeLink"
      class="spa-cinema-thumbnail-link"
      type="button"
      aria-label="${title ? `${globalStrings && globalStrings.play ? globalStrings.play : 'Play video'}: ${title}` : `${globalStrings && globalStrings.play ? globalStrings.play : 'Play video'}`}"
    >
      ${imgOutput}
      ${playBtnIcon}
    </button>
  `;

  // Check if output just needs to be the image when the player is loaded
  if (autoEmbed.toString() === 'true') {
    output = imgOutput;
  }
  return new Handlebars.SafeString(`${output.replace(/[\t\n\r]/gm, '')}`);
}

export { cinemaHelper, cinemaThumbnailHelper };
